import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AppLayout } from '../components/AppLayout'
import { DiamondCard } from '../components/DiamondCard'
import { MediaModal } from '../components/MediaModal'
import { favoritesApi, favoriteStonesApi, searchApi, type FavoriteSupplier } from '../api/endpoints'
import { ApiError } from '../api/types'
import type { Diamond } from '../api/types'
import { setFavoriteStone } from '../lib/sessionCache'
import { useApp } from '../store'
import { toast } from '../lib/toast'
import { supplierIntroText, waHref, smsHref, mailtoHref } from '../lib/contactMessage'

type Tab = 'stones' | 'suppliers'

/**
 * /favorites — the buyer's saved items (Tej 2026-08). Two tabs:
 *   • Saved stones — diamonds bookmarked from the detail. The server stores
 *     ONLY the stockId; this page re-fetches each stone's CURRENT data through
 *     the marketplace API (searchApi.detail → applyMarketplace) so prices stay
 *     KYC-gated and reflect any live change. A sold/delisted stone comes back
 *     404 → a distinct "No longer available" card (never silently dropped).
 *   • Suppliers — the contact book: WhatsApp / email / SMS open PREFILLED with
 *     a DiaEx intro for one-tap outreach.
 */
export function Favorites() {
  const [tab, setTab] = useState<Tab>(
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('tab') === 'suppliers'
      ? 'suppliers' : 'stones',
  )
  return (
    <AppLayout>
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8 sm:py-10">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold">Buyer</p>
            <h1 className="mt-1 font-serif text-3xl font-bold tracking-tight text-ink">Favorites</h1>
            <p className="mt-1 text-sm text-soft">Stones you&rsquo;ve saved and suppliers you&rsquo;ve hearted.</p>
          </div>
          <Link
            to="/search"
            className="inline-flex h-10 shrink-0 items-center rounded-full bg-navy px-5 text-sm font-semibold text-white no-underline hover:bg-navy-hover active:scale-[0.98] transition-all"
          >
            Browse diamonds
          </Link>
        </div>

        <div className="mt-6 flex gap-1 border-b border-line" role="tablist">
          <TabButton active={tab === 'stones'} onClick={() => setTab('stones')} testid="fav-tab-stones">
            Saved stones
          </TabButton>
          <TabButton active={tab === 'suppliers'} onClick={() => setTab('suppliers')} testid="fav-tab-suppliers">
            Suppliers
          </TabButton>
        </div>

        <div className="mt-8">
          {tab === 'stones' ? <SavedStones /> : <SavedSuppliers />}
        </div>
      </div>
    </AppLayout>
  )
}

function TabButton({ active, onClick, testid, children }: { active: boolean; onClick: () => void; testid: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
        active ? 'border-gold text-ink' : 'border-transparent text-soft hover:text-ink'
      }`}
    >
      {children}
    </button>
  )
}

/** One saved stone's live state: loading, its fresh data, or "gone" (404). */
type SavedRow =
  | { stockId: string; state: 'loading' }
  | { stockId: string; state: 'ok'; stone: Diamond }
  | { stockId: string; state: 'gone' }

function SavedStones() {
  const signedIn = useApp((s) => s.signedIn)
  const [rows, setRows] = useState<SavedRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [openStone, setOpenStone] = useState<Diamond | null>(null)

  // Load the identifier list, then re-fetch each stone LIVE. We never trust a
  // stored price/availability — everything is derived from the fresh fetch,
  // which runs through applyMarketplace server-side.
  useEffect(() => {
    let cancelled = false
    setRows(null)
    setError(null)
    favoriteStonesApi.list()
      .then(async ({ stones }) => {
        if (cancelled) return
        if (stones.length === 0) { setRows([]); return }
        // Seed the grid as loading (newest first — server already sorts).
        setRows(stones.map((s) => ({ stockId: s.stockId, state: 'loading' as const })))
        // Re-fetch each stone's current data. A 404 = sold/delisted → "gone".
        await Promise.all(stones.map(async (s) => {
          let resolved: SavedRow
          try {
            const stone = await searchApi.detail(s.stockId)
            resolved = { stockId: s.stockId, state: 'ok', stone }
          } catch (e) {
            if (e instanceof ApiError && e.status === 404) {
              resolved = { stockId: s.stockId, state: 'gone' }
            } else {
              // Transient (network/5xx): show it as gone-with-retry-copy would
              // over-promise; treat like unavailable so the card still renders
              // with a Remove action rather than vanishing.
              resolved = { stockId: s.stockId, state: 'gone' }
            }
          }
          if (cancelled) return
          setRows((prev) => prev
            ? prev.map((r) => (r.stockId === s.stockId ? resolved : r))
            : prev)
        }))
      })
      .catch((e) => {
        if (cancelled) return
        setError(e instanceof ApiError && e.status === 401
          ? 'Sign in to see your saved stones.'
          : 'Could not load your saved stones.')
        setRows([])
      })
    return () => { cancelled = true }
  }, [])

  async function remove(stockId: string) {
    try {
      await favoriteStonesApi.toggle(stockId)
      setFavoriteStone(stockId, false) // keep the session cache + every bookmark in sync
      setRows((prev) => (prev ? prev.filter((r) => r.stockId !== stockId) : prev))
      toast('Removed from saved', 'success')
    } catch {
      toast('Could not remove — try again.', 'error')
    }
  }

  if (rows === null) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" aria-hidden>
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-72 rounded-md skeleton" />)}
      </div>
    )
  }
  if (error) {
    return <div className="rounded-card border border-danger/30 bg-danger/5 p-6 text-sm text-danger">{error}</div>
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-card border border-border bg-paper p-10 text-center" data-testid="saved-stones-empty">
        <p className="font-serif text-xl text-ink">No saved stones yet</p>
        <p className="mt-2 text-sm text-soft">
          Open any diamond and tap <span className="font-semibold text-ink">Save</span> in the header —
          it&rsquo;ll show up here with its current price.
        </p>
        <Link to="/search" className="mt-4 inline-flex btn-primary px-6 no-underline">Browse diamonds</Link>
      </div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="saved-stones-grid">
        {rows.map((r) => {
          if (r.state === 'loading') {
            return <div key={r.stockId} className="h-72 rounded-md skeleton" aria-hidden />
          }
          if (r.state === 'gone') {
            return <GoneCard key={r.stockId} stockId={r.stockId} onRemove={() => void remove(r.stockId)} />
          }
          return (
            <div key={r.stockId} className="relative" data-testid="saved-stone-card">
              <DiamondCard stone={r.stone} blurPrice={!signedIn} onOpen={setOpenStone} />
              <RemoveButton onClick={() => void remove(r.stockId)} />
            </div>
          )
        })}
      </div>
      <MediaModal
        open={!!openStone}
        onClose={() => setOpenStone(null)}
        stone={openStone}
        blurPrice={!signedIn}
      />
    </>
  )
}

/** A saved stone that no longer comes back from the API — sold or delisted. */
function GoneCard({ stockId, onRemove }: { stockId: string; onRemove: () => void }) {
  return (
    <div
      className="relative flex h-72 flex-col items-center justify-center gap-2 rounded-md border border-line bg-cream/60 p-4 text-center opacity-90"
      data-testid="saved-stone-gone"
    >
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-navy-100 text-navy-400">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="9" /><line x1="8" y1="12" x2="16" y2="12" />
        </svg>
      </span>
      <p className="text-sm font-semibold text-ink mono-id">#{stockId}</p>
      <p className="text-[12px] text-soft">No longer available — sold or delisted by the supplier.</p>
      <button
        type="button"
        onClick={onRemove}
        className="mt-1 inline-flex h-8 items-center rounded-full border border-border bg-paper px-4 text-[12px] font-semibold text-ink hover:border-danger/50 hover:text-danger transition-colors"
        data-testid="saved-stone-gone-remove"
      >
        Remove
      </button>
    </div>
  )
}

/** Small "×" overlay to unsave a still-available card without opening it. */
function RemoveButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Remove from saved"
      title="Remove from saved"
      data-testid="saved-stone-remove"
      className="absolute top-2 right-2 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-paper/95 text-soft shadow-elev-1 backdrop-blur-sm hover:bg-navy-700 hover:text-white transition-colors"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <line x1="6" y1="6" x2="18" y2="18" /><line x1="6" y1="18" x2="18" y2="6" />
      </svg>
    </button>
  )
}

// ─── Suppliers tab (the original /favorites contact book) ───────────────────

function SavedSuppliers() {
  const [suppliers, setSuppliers] = useState<FavoriteSupplier[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    favoritesApi.list()
      .then((r) => { if (!cancelled) { setSuppliers(r.suppliers); setError(null) } })
      .catch((e) => {
        if (cancelled) return
        setError(e instanceof ApiError && e.status === 401
          ? 'Sign in to see your favorite suppliers.'
          : 'Could not load your favorites.')
        setSuppliers([])
      })
    return () => { cancelled = true }
  }, [])

  async function remove(supplierId: number) {
    try {
      await favoritesApi.toggle({ supplierId })
      setSuppliers((prev) => (prev ? prev.filter((s) => s.supplierId !== supplierId) : prev))
      toast('Removed from favorites', 'success')
    } catch {
      toast('Could not remove — try again.', 'error')
    }
  }

  if (suppliers === null) {
    return (
      <div className="space-y-3" aria-hidden>
        {[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-xl skeleton" />)}
      </div>
    )
  }
  if (error) {
    return <div className="rounded-card border border-danger/30 bg-danger/5 p-6 text-sm text-danger">{error}</div>
  }
  if (suppliers.length === 0) {
    return (
      <div className="rounded-card border border-border bg-paper p-10 text-center">
        <p className="font-serif text-xl text-ink">No favorite suppliers yet</p>
        <p className="mt-2 text-sm text-soft">
          Open any stone and tap the <span className="font-semibold text-ink">heart</span> next to the
          supplier&rsquo;s name — they&rsquo;ll show up here for quick contact.
        </p>
        <Link to="/search" className="mt-4 inline-flex btn-primary px-6 no-underline">Browse diamonds</Link>
      </div>
    )
  }

  return (
    <ul className="space-y-3">
      {suppliers.map((s) => {
        const intro = supplierIntroText(s.contactName || s.name)
        return (
          <li
            key={s.supplierId}
            className="rounded-xl border border-line bg-paper p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
            data-testid="favorite-row"
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy font-serif text-lg font-bold text-white">
                {(s.name || 'D').charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink">{s.name || `Supplier #${s.supplierId}`}</p>
                <p className="mt-0.5 truncate text-[12px] text-soft">
                  {[s.contactName && s.contactName !== s.name ? `Attn: ${s.contactName}` : null, s.country, s.email, s.phone]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {s.phone && (
                <a
                  href={waHref(s.phone, intro)} target="_blank" rel="noopener"
                  className="inline-flex h-9 items-center rounded-full bg-navy px-3.5 text-[12px] font-semibold text-white no-underline hover:bg-navy-hover transition-colors"
                >
                  WhatsApp
                </a>
              )}
              {s.email && (
                <a
                  href={mailtoHref(s.email, 'DiaEx — buyer enquiry', intro)}
                  className="inline-flex h-9 items-center rounded-full border border-border bg-paper px-3.5 text-[12px] font-semibold text-ink no-underline hover:border-gold/50 hover:bg-cream transition-colors"
                >
                  Email
                </a>
              )}
              {s.phone && (
                <>
                  <a
                    href={`tel:${s.phone}`}
                    className="inline-flex h-9 items-center rounded-full border border-border bg-paper px-3.5 text-[12px] font-semibold text-ink no-underline hover:border-gold/50 hover:bg-cream transition-colors"
                  >
                    Call
                  </a>
                  <a
                    href={smsHref(s.phone, intro)}
                    className="inline-flex h-9 items-center rounded-full border border-border bg-paper px-3.5 text-[12px] font-semibold text-ink no-underline hover:border-gold/50 hover:bg-cream transition-colors"
                  >
                    Text
                  </a>
                </>
              )}
              <button
                type="button" onClick={() => void remove(s.supplierId)}
                aria-label="Remove from favorites"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-soft hover:text-danger hover:bg-page transition-colors"
                data-testid="favorite-remove"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
